import { useEffect, useRef } from 'react';
import { useAuth } from '../Auth';
import { useTasks, useProjects, useCreateTask } from '../hooks';

/**
 * Auto-creates milestone tasks for each project's current working week
 * if they don't already exist. Runs once on dashboard load.
 */
export function useAutoCreateWeeklyTasks() {
  const { user, selectedGroup } = useAuth();
  const hasRun = useRef(false);
  const createTask = useCreateTask();

  // Fetch tasks and projects; only enable project query when we have a group
  const { data: tasks = [] } = useTasks();
  const { data: projects = [] } = useProjects(
    selectedGroup ? { groupId: selectedGroup.id } : undefined
  );

  useEffect(() => {
    // Guard: only run once, only when we have a user
    if (hasRun.current || !user) return;

    // Don't run if we're still loading data
    if (projects.length === 0) return;

    // Mark as run so we don't keep creating
    hasRun.current = true;

    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    // Format as YYYY-MM-DD
    const formatDate = (d: Date) => d.toISOString().split('T')[0];

    // Check if we already have a weekly review task for this week
    const weekKey = `week-${formatDate(startOfWeek)}`;
    const existingWeeklyTask = tasks.find((t: any) => t.key === weekKey);

    if (existingWeeklyTask) return; // Already have a weekly task

    // Create a single weekly milestone task anchored to the first project
    const primaryProject = projects[0];

    createTask.mutate(
      {
        key: weekKey,
        title: `Weekly Review: Week of ${formatDate(startOfWeek)}`,
        description: `Automated weekly review task for the week of ${formatDate(startOfWeek)}. Review progress on all projects and plan next steps.`,
        status: 'pending',
        taskType: 'milestone',
        objectId: primaryProject.id,
        objectType: 'project',
        groupId: selectedGroup?.id,
        dueAt: endOfWeek.toISOString(),
      },
      {
        onError: (err) => {
          // Task might already exist from a concurrent session — reset so we can retry next load
          console.warn('Auto-task creation failed:', err);
          hasRun.current = false;
        },
      }
    );
  }, [user, selectedGroup, tasks.length, projects.length]);
}